/**
 * §5.4 — persistent standing on the player device.
 *
 * The tier drives presentation only. Rank itself is always computed by the
 * server (§21.1) and is only recalculated after a question is scored, never
 * mid-question, so it cannot leak another player's answer (§5.4).
 */

export type StandingTier = "medal" | "highlight" | "plain" | "unranked";

export interface Standing {
  /** 1-based rank, or null before the player's first scored answer. */
  position: number | null;
  /** Total number of ranked participants. */
  participants: number;
  tier: StandingTier;
  /** Ready-to-render label, per the §5.4 table. */
  label: string;
  /** §13.2 — points to the next rank up, null when 1st or unranked. */
  pointsToNextRank: number | null;
  /** §12.4 — rank movement since the previous question. Positive is upward. */
  rankDelta: number | null;
}

export function standingTier(position: number | null): StandingTier {
  if (position === null) return "unranked";
  if (position <= 3) return "medal";
  if (position <= 10) return "highlight";
  return "plain";
}

export function standingLabel(position: number | null, participants: number): string {
  if (position === null) return `— of ${participants}`;
  if (position <= 10) return ordinal(position);
  return `${position} of ${participants}`;
}

export function buildStanding(input: {
  position: number | null;
  participants: number;
  pointsToNextRank?: number | null;
  rankDelta?: number | null;
}): Standing {
  const tier = standingTier(input.position);
  return {
    position: input.position,
    participants: input.participants,
    tier,
    label: standingLabel(input.position, input.participants),
    pointsToNextRank: input.pointsToNextRank ?? null,
    rankDelta: input.rankDelta ?? null,
  };
}

export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** §12.4 — "Up 4 places" / "Down 2 places" / "No change". */
export function describeRankDelta(delta: number | null): string {
  if (delta === null || delta === 0) return "No change";
  const places = Math.abs(delta) === 1 ? "place" : "places";
  return delta > 0 ? `Up ${delta} ${places}` : `Down ${Math.abs(delta)} ${places}`;
}
